"use client";

import React, { useRef } from "react";
import {
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  HStack,
  Text,
  useToast,
  IconButton,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  VStack,
  ButtonGroup,
  Box,
  Flex,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Checkbox,
} from "@chakra-ui/react";
import { FaPlayCircle, FaStopCircle } from "react-icons/fa";
import { ChevronDownIcon, SettingsIcon } from "@chakra-ui/icons";
import {
  agentService,
  type VoiceDetails,
  type RetellLLM,
} from "../../voice-services/agentService";

import GenderImage from "./voices/GenderImages";

const REALTIME_SUPPORTED_VOICES = [
  "openai-Adrian",
  "openai-Alloy",
  "openai-Amy",
  "openai-Andrew",
  "openai-Anna",
  "openai-Anthony",
  "openai-Ash",
  "openai-Ballad",
  "openai-Brian",
  "openai-Carola",
  "openai-Cedar",
  "openai-Chloe",
  "openai-Cimo",
  "openai-Coral",
  "openai-Echo",
  "openai-Emily",
  "openai-Grace",
  "openai-Julia",
  "openai-Kate",
  "openai-Kathrine",
  "openai-Marin",
  "openai-Marissa",
  "openai-Monika",
  "openai-Myra",
  "openai-Paola",
  "openai-Sage",
  "openai-Santiago",
  "openai-Shimmer",
  "openai-Susan",
  "openai-Verse",
  "openai-Zuri",
];

const ELEVENLABS_VOICE_MODELS = [
  {
    id: "eleven_v3",
    label: "Elevenlabs V3",
    description: "Multilingual, slow, highest quality",
  },
  {
    id: "eleven_turbo_v2",
    label: "Elevenlabs Turbo V2",
    description: "English only, fast, high quality",
  },
  {
    id: "eleven_flash_v2",
    label: "Elevenlabs Flash V2",
    description: "English only, fastest, medium quality",
  },
  {
    id: "eleven_turbo_v2_5",
    label: "Elevenlabs Turbo V2.5",
    description: "Multilingual, fast, high quality",
  },
  {
    id: "eleven_flash_v2_5",
    label: "Elevenlabs Flash V2.5",
    description: "Multilingual, fastest, medium quality",
  },
  {
    id: "eleven_multilingual_v2",
    label: "Elevenlabs Multilingual v2",
    description: "Multilingual, slow, highest quality",
  },
];

interface VoiceSelectProps {
  initialVoice: VoiceDetails | null;
  voices: VoiceDetails[];
  agentId: string;
  setSelectedVoice: React.Dispatch<React.SetStateAction<VoiceDetails | null>>;
  currentModel?: RetellLLM | null;
}

interface VoiceSettings {
  speed: number;
  temperature: number;
  volume: number;
  voice_model: string | null;
  enable_dynamic_voice_speed: boolean;
}

export const VoiceSelect: React.FC<VoiceSelectProps> = React.memo(
  ({ initialVoice, voices, agentId, setSelectedVoice, currentModel }) => {
    const [selectedVoice, setSelectedVoiceState] =
      React.useState<VoiceDetails | null>(initialVoice);

    React.useEffect(() => {
      if (initialVoice) {
        setSelectedVoiceState(initialVoice);
      }
    }, [initialVoice]);

    const [isSettingsOpen, setIsSettingsOpen] = React.useState(false);
    const [currentlyPlaying, setCurrentlyPlaying] = React.useState<
      string | null
    >(null);
    const [isMenuOpen, setIsMenuOpen] = React.useState(false);
    const audioRef = React.useRef<HTMLAudioElement | null>(null);
    const toast = useToast();

    const previousProviderRef = useRef<string | null | undefined>(
      initialVoice?.provider || null,
    );

    const [voiceSettings, setVoiceSettings] = React.useState<VoiceSettings>({
      speed: 1.0,
      temperature: 1.0,
      volume: 1.0,
      voice_model: null,
      enable_dynamic_voice_speed: false,
    });
    const [tempSettings, setTempSettings] =
      React.useState<VoiceSettings>(voiceSettings);

    const isRealtimeModel = React.useMemo(() => {
      if (!currentModel) return false;
      return !!(
        currentModel.s2s_model && currentModel.s2s_model.includes("realtime")
      );
    }, [currentModel]);

    const filteredVoices = React.useMemo(() => {
      if (isRealtimeModel) {
        return voices.filter((voice) =>
          REALTIME_SUPPORTED_VOICES.includes(voice.voice_id),
        );
      }
      return voices;
    }, [voices, isRealtimeModel]);

    // Get unique providers from filtered voices array
    const providers = React.useMemo(() => {
      const uniqueProviders = Array.from(
        new Set(
          filteredVoices
            .map((voice) => voice.provider)
            .filter(
              (provider) =>
                provider !== "playht" &&
                provider !== "cartesia" &&
                provider !== "minimax",
            ),
        ),
      );
      return uniqueProviders.sort();
    }, [filteredVoices]);

    const isElevenLabsVoice = React.useMemo(() => {
      return selectedVoice?.provider === "elevenlabs";
    }, [selectedVoice]);

    // ✅ Retell-safe compatibility: voice_model selection is only supported for ElevenLabs in our implementation
    const isVoiceModelCompatible = (
      provider: string | undefined,
      voiceModel: string | null,
    ): boolean => {
      if (!provider || !voiceModel) return false;
      if (provider === "elevenlabs") return voiceModel.startsWith("eleven_");
      return false;
    };

    React.useEffect(() => {
      audioRef.current = new Audio();

      return () => {
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current = null;
        }
      };
    }, []);

    React.useEffect(() => {
      if (!isMenuOpen && audioRef.current && currentlyPlaying) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        setCurrentlyPlaying(null);
      }
    }, [isMenuOpen, currentlyPlaying]);

    const playAudio = React.useCallback(
      (voiceId: string, audioUrl: string | undefined, e: React.MouseEvent) => {
        e.stopPropagation();

        if (!audioUrl) {
          toast({
            title: "Preview not available",
            description: "No preview audio available for this voice",
            status: "info",
            duration: 2000,
          });
          return;
        }

        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
        }

        if (currentlyPlaying === voiceId) {
          setCurrentlyPlaying(null);
          return;
        }

        if (audioRef.current) {
          audioRef.current.src = audioUrl;
          audioRef.current.play().catch((error) => {
            console.error("Error playing audio:", error);
            toast({
              title: "Error playing preview",
              description: "Could not play the audio preview",
              status: "error",
              duration: 2000,
            });
          });

          setCurrentlyPlaying(voiceId);

          audioRef.current.onended = () => {
            setCurrentlyPlaying(null);
          };
        }
      },
      [currentlyPlaying, toast],
    );

    const fetchAgentDetails = React.useCallback(async () => {
      try {
        const data = await agentService.getAgentDetails(agentId);
        let voiceModel = data.voice_model as any;

        // ✅ Retell-safe defaults:
        // - Do NOT set PlayHT models (Retell voice_model enum doesn't include them)
        // - Only default ElevenLabs to eleven_turbo_v2
        if (!voiceModel) {
          if (isElevenLabsVoice) {
            voiceModel = "eleven_turbo_v2";
          } else {
            voiceModel = null;
          }
        } else {
          // If current voice model isn't compatible, drop it
          if (isElevenLabsVoice && typeof voiceModel === "string") {
            if (!voiceModel.startsWith("eleven_")) voiceModel = null;
          } else {
            // Non-elevenlabs provider => don't keep a voice_model
            voiceModel = null;
          }
        }

        setVoiceSettings({
          speed: data.voice_speed !== undefined ? data.voice_speed : 1.0,
          temperature:
            data.voice_temperature !== undefined ? data.voice_temperature : 1.0,
          volume: data.volume !== undefined ? data.volume : 1.0,
          voice_model: voiceModel,
          enable_dynamic_voice_speed: data.enable_dynamic_voice_speed === true,
        });
        setTempSettings({
          speed: data.voice_speed !== undefined ? data.voice_speed : 1.0,
          temperature:
            data.voice_temperature !== undefined ? data.voice_temperature : 1.0,
          volume: data.volume !== undefined ? data.volume : 1.0,
          voice_model: voiceModel,
          enable_dynamic_voice_speed: data.enable_dynamic_voice_speed === true,
        });
      } catch (error) {
        console.error("Error fetching agent details:", error);
        toast({
          title: "Error fetching agent details",
          description: "Unable to load current voice settings",
          status: "error",
          duration: 3000,
        });
      }
    }, [agentId, isElevenLabsVoice, toast]);

    const handleSettingsClick = () => {
      fetchAgentDetails();
      setIsSettingsOpen(true);
    };

    const handleVoiceChange = React.useCallback(
      async (voice: VoiceDetails) => {
        try {
          const currentProvider = selectedVoice?.provider;
          const newProvider = voice.provider;
          const hasProviderChanged = currentProvider !== newProvider;

          if (hasProviderChanged) {
            await agentService.update(agentId, {
              voice_id: voice.voice_id,
              voice_model: null,
            });
            previousProviderRef.current = newProvider;
          } else {
            await agentService.update(agentId, {
              voice_id: voice.voice_id,
            });
          }

          setSelectedVoiceState(voice);
          setSelectedVoice(voice);
          setIsMenuOpen(false);
          toast({
            title: "Voice updated successfully",
            status: "success",
            duration: 2000,
          });
        } catch (error: any) {
          console.error("Error updating voice:", error);
          const errorMessage =
            error.response?.data?.message ||
            error.message ||
            "An unexpected error occurred";
          toast({
            title: "Error updating voice",
            description: errorMessage,
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }
      },
      [agentId, selectedVoice, setSelectedVoice, toast],
    );

    const handleSettingsSave = async () => {
      try {
        const isCompatible = isVoiceModelCompatible(
          selectedVoice?.provider,
          tempSettings.voice_model,
        );

        const currentProvider = selectedVoice?.provider;
        const previousProvider = previousProviderRef.current;
        const hasProviderChanged = currentProvider !== previousProvider;

        const requestBody: any = {
          voice_speed: tempSettings.speed,
          voice_temperature: tempSettings.temperature,
          volume: tempSettings.volume,
          enable_dynamic_voice_speed: tempSettings.enable_dynamic_voice_speed,
        };

        // ✅ Only include voice_model for ElevenLabs, compatible, and provider unchanged
        if (isCompatible && !hasProviderChanged) {
          requestBody.voice_model = tempSettings.voice_model;
        } else {
          requestBody.voice_model = null;
        }

        await agentService.updateAgentSettings(agentId, requestBody);

        previousProviderRef.current = currentProvider;

        setVoiceSettings(tempSettings);
        setIsSettingsOpen(false);
        toast({
          title: "Voice settings updated successfully",
          status: "success",
          duration: 2000,
        });
      } catch (error: any) {
        console.error("Error updating voice settings:", error);
        toast({
          title: "Error updating voice settings",
          description:
            error.response?.data?.message || "An unexpected error occurred",
          status: "error",
          duration: 3000,
        });
      }
    };

    const handleSettingsCancel = () => {
      setTempSettings(voiceSettings);
      setIsSettingsOpen(false);
    };

    const formatProviderName = (provider: string) => {
      return provider.charAt(0).toUpperCase() + provider.slice(1);
    };

    const renderVoiceItem = (voice: VoiceDetails) => (
      <MenuItem
        key={voice.voice_id}
        onClick={() => handleVoiceChange(voice)}
        bg="transparent"
        _hover={{ bg: "primary.50" }}
        _focus={{ bg: "primary.50" }}
        _active={{ bg: "primary.100" }}
        color="black"
        py={3}
      >
        <Flex direction="column" width="100%">
          <Flex align="center" width="100%">
            <IconButton
              aria-label="Play voice sample"
              icon={
                <Box
                  as="span"
                  fontSize="sm"
                  color="gray.600"
                  borderRadius="full"
                >
                  {currentlyPlaying === voice.voice_id ? (
                    <FaStopCircle />
                  ) : (
                    <FaPlayCircle />
                  )}
                </Box>
              }
              size="xs"
              variant="ghost"
              mr={2}
              onClick={(e) =>
                playAudio(voice.voice_id, voice.preview_audio_url, e)
              }
            />
            <GenderImage voice={voice} />
            <Text fontWeight="medium">{voice?.voice_name}</Text>
          </Flex>
          <Flex ml="36px" mt={1} fontSize="xs" color="gray.600">
            <Text mr={2}>{voice.gender}</Text>
            {voice.accent && (
              <>
                <Text mr={2}>•</Text>
                <Text mr={2}>{voice.accent}</Text>
              </>
            )}
            {voice.age && (
              <>
                <Text mr={2}>•</Text>
                <Text>{voice.age}</Text>
              </>
            )}
          </Flex>
        </Flex>
      </MenuItem>
    );

    return (
      <HStack spacing={1}>
        <Menu
          isOpen={isMenuOpen}
          onOpen={() => setIsMenuOpen(true)}
          onClose={() => setIsMenuOpen(false)}
        >
          <MenuButton
            as={Button}
            variant="outline"
            size="sm"
            rightIcon={<ChevronDownIcon />}
            colorScheme="primary"
            borderColor="primary.400"
            color="gray.800"
            _hover={{ bg: "primary.50", borderColor: "primary.500" }}
            _active={{ bg: "primary.100", borderColor: "primary.600" }}
            _focusVisible={{
              borderColor: "primary.500",
              boxShadow: "0 0 0 1px var(--chakra-colors-primary-500)",
            }}
          >
            <HStack spacing={2} color="black">
              <GenderImage voice={selectedVoice} />
              <Text>{selectedVoice?.voice_name || "Select Voice"}</Text>
            </HStack>
          </MenuButton>

          <MenuList
            bg="white"
            borderColor="primary.200"
            _focusWithin={{
              borderColor: "primary.300",
              boxShadow: "0 0 0 1px var(--chakra-colors-primary-300)",
            }}
            p={0}
            sx={{
              "&::-webkit-scrollbar": { width: "8px" },
              "&::-webkit-scrollbar-track": { background: "transparent" },
              "&::-webkit-scrollbar-thumb": {
                background: "var(--chakra-colors-primary-400)",
                borderRadius: "6px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "var(--chakra-colors-primary-500)",
              },
              scrollbarWidth: "thin",
              scrollbarColor: "var(--chakra-colors-primary-400) transparent",
            }}
          >
            <Tabs variant="enclosed" isFitted size="sm">
              <TabList>
                {providers.map((provider) => (
                  <Tab
                    key={provider}
                    _selected={{
                      color: "primary.800",
                      bg: "primary.100",
                      fontWeight: "semibold",
                    }}
                    color="gray.600"
                    bg="white"
                    borderBottomWidth="1px"
                    borderBottomColor="gray.200"
                    _hover={{ bg: "primary.50" }}
                  >
                    {formatProviderName(provider)}
                  </Tab>
                ))}
              </TabList>
              <TabPanels>
                {providers.map((provider) => (
                  <TabPanel key={provider} p={0} maxH="300px" overflowY="auto">
                    {filteredVoices
                      .filter((voice) => voice.provider === provider)
                      .map(renderVoiceItem)}
                  </TabPanel>
                ))}
              </TabPanels>
            </Tabs>
          </MenuList>
        </Menu>

        <Popover
          isOpen={isSettingsOpen}
          onClose={() => setIsSettingsOpen(false)}
          placement="bottom-start"
          closeOnBlur={false}
        >
          <PopoverTrigger>
            <IconButton
              colorScheme="primary"
              borderColor="primary.400"
              color="primary.600"
              _hover={{ bg: "primary.50", borderColor: "primary.500" }}
              _active={{ bg: "primary.100", borderColor: "primary.600" }}
              _focusVisible={{
                borderColor: "primary.500",
                boxShadow: "0 0 0 1px var(--chakra-colors-primary-500)",
              }}
              aria-label="Voice settings"
              icon={<SettingsIcon />}
              size="sm"
              variant="outline"
              onClick={handleSettingsClick}
            />
          </PopoverTrigger>
          <PopoverContent
            bg="white"
            borderColor="primary.200"
            _focusWithin={{
              borderColor: "primary.300",
              boxShadow: "0 0 0 1px var(--chakra-colors-primary-300)",
            }}
            w="350px"
          >
            <PopoverBody p={4}>
              <VStack spacing={4} align="stretch">
                {/* ✅ Only show voice model picker for ElevenLabs */}
                {isElevenLabsVoice && (
                  <Box>
                    <Text fontWeight="medium" mb={2} color="black">
                      Voice Model
                    </Text>
                    <Menu placement="bottom-start" closeOnSelect>
                      <MenuButton
                        as={Button}
                        rightIcon={<ChevronDownIcon />}
                        size="sm"
                        w="full"
                        justifyContent="space-between"
                        fontWeight="normal"
                        color="black"
                        bg="white"
                        borderWidth="1px"
                        borderColor="gray.200"
                        _hover={{ bg: "gray.50", borderColor: "gray.300" }}
                        _expanded={{
                          bg: "primary.50",
                          borderColor: "primary.400",
                        }}
                      >
                        {
                          ELEVENLABS_VOICE_MODELS.find(
                            (m) =>
                              m.id ===
                              (tempSettings.voice_model ?? "eleven_turbo_v2"),
                          )?.label
                        }
                      </MenuButton>
                      <MenuList
                        minW="320px"
                        maxH="280px"
                        overflowY="auto"
                        borderColor="gray.200"
                      >
                        {ELEVENLABS_VOICE_MODELS.map((model) => (
                          <MenuItem
                            key={model.id}
                            onClick={() =>
                              setTempSettings((prev) => ({
                                ...prev,
                                voice_model: model.id,
                              }))
                            }
                            bg={
                              (tempSettings.voice_model ??
                                "eleven_turbo_v2") === model.id
                                ? "primary.50"
                                : "transparent"
                            }
                            _hover={{ bg: "primary.50" }}
                          >
                            <Flex direction="column" align="stretch" gap={0}>
                              <Text fontWeight="medium" color="black">
                                {model.label}
                              </Text>
                              <Text fontSize="xs" color="gray.500">
                                {model.description}
                              </Text>
                            </Flex>
                          </MenuItem>
                        ))}
                      </MenuList>
                    </Menu>
                    <Box
                      borderBottomWidth="1px"
                      borderColor="gray.200"
                      my={2}
                    />
                  </Box>
                )}

                <Box>
                  <Flex justify="space-between" align="center" mb={1}>
                    <Text fontWeight="medium" color="black">
                      Voice Speed
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      {tempSettings.speed.toFixed(2)}
                    </Text>
                  </Flex>
                  <Slider
                    value={tempSettings.speed}
                    onChange={(value) =>
                      setTempSettings((prev) => ({ ...prev, speed: value }))
                    }
                    min={0.5}
                    max={2.0}
                    step={0.01}
                    focusThumbOnChange={false}
                    isDisabled={tempSettings.enable_dynamic_voice_speed}
                  >
                    <SliderTrack bg="gray.200">
                      <SliderFilledTrack bg="primary.500" />
                    </SliderTrack>
                    <SliderThumb boxSize={4} />
                  </Slider>
                  <Checkbox
                    mt={2}
                    isChecked={tempSettings.enable_dynamic_voice_speed}
                    onChange={(e) =>
                      setTempSettings((prev) => ({
                        ...prev,
                        enable_dynamic_voice_speed: e.target.checked,
                      }))
                    }
                    colorScheme="primary"
                    size="sm"
                  >
                    <Text as="span" fontSize="sm" color="black">
                      Dynamically adjust based on user input
                    </Text>
                  </Checkbox>
                </Box>

                <Box>
                  <Flex justify="space-between" align="center" mb={1}>
                    <Text fontWeight="medium" color="black">
                      Voice Temperature
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      {tempSettings.temperature.toFixed(2)}
                    </Text>
                  </Flex>
                  <Slider
                    value={tempSettings.temperature}
                    onChange={(value) =>
                      setTempSettings((prev) => ({
                        ...prev,
                        temperature: value,
                      }))
                    }
                    min={0}
                    max={2.0}
                    step={0.01}
                    focusThumbOnChange={false}
                  >
                    <SliderTrack bg="gray.200">
                      <SliderFilledTrack bg="primary.500" />
                    </SliderTrack>
                    <SliderThumb boxSize={4} />
                  </Slider>
                </Box>

                <Box>
                  <Flex justify="space-between" align="center" mb={1}>
                    <Text fontWeight="medium" color="black">
                      Voice Volume
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      {tempSettings.volume.toFixed(2)}
                    </Text>
                  </Flex>
                  <Slider
                    value={tempSettings.volume}
                    onChange={(value) =>
                      setTempSettings((prev) => ({ ...prev, volume: value }))
                    }
                    min={0}
                    max={2.0}
                    step={0.01}
                    focusThumbOnChange={false}
                  >
                    <SliderTrack bg="gray.200">
                      <SliderFilledTrack bg="primary.500" />
                    </SliderTrack>
                    <SliderThumb boxSize={4} />
                  </Slider>
                </Box>

                <ButtonGroup
                  size="sm"
                  w="full"
                  justifyContent="flex-end"
                  spacing={2}
                >
                  <Button
                    variant="ghost"
                    onClick={handleSettingsCancel}
                    color="black"
                    _hover={{ bg: "gray.100" }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSettingsSave}
                    colorScheme="primary"
                    bg="primary.500"
                    _hover={{ bg: "primary.600" }}
                  >
                    Save
                  </Button>
                </ButtonGroup>
              </VStack>
            </PopoverBody>
          </PopoverContent>
        </Popover>
      </HStack>
    );
  },
);

VoiceSelect.displayName = "VoiceSelect";
